import {
  EncodedFrameSizeTracker,
  isEncodedFrameBatch,
  type EncodedFrameSummary,
  type EncodedFrameTap,
} from '@playcastdotio/PeerCore';

/** The receiver surface this tap needs: the standard `transform` slot. */
type TransformableReceiver = { transform?: unknown };

/** The constructor this tap feature-detects; absent on engines without encoded transforms. */
type ScriptTransformCtor = new (worker: Worker, options?: unknown) => unknown;

/**
 * Produces the worker. Injected so tests stand in a fake; the default resolves the worker
 * module lazily because its URL comes from `import.meta`, which the test build cannot parse.
 */
export type EncodedFrameWorkerFactory = () => Worker | Promise<Worker>;

async function defaultWorkerFactory(): Promise<Worker> {
  const { createEncodedFrameWorker } = await import('./encodedFrameWorkerFactory');
  return createEncodedFrameWorker();
}

function scriptTransformCtor(): ScriptTransformCtor | null {
  const ctor = (globalThis as Record<string, unknown>)['RTCRtpScriptTransform'];
  return typeof ctor === 'function' ? (ctor as ScriptTransformCtor) : null;
}

/** What the worker has told us so far; each flag flips once and stays. */
type WorkerHandshake = { ready: boolean; transform: boolean };

/**
 * EncodedFrameStatsTap - the app half of the encoded-frame size instrument.
 *
 * Owns the Worker and the RTCRtpScriptTransform that puts it on the video receiver; folds
 * the batches the worker posts into a PeerCore tracker and hands the session one summary per
 * clientStats tick.
 *
 * The worker is spawned at construction, not at attach, so the transform is set synchronously
 * inside the track event, before the first frame. A fresh spare is prepared after each attach
 * so a reconnect's new receiver gets the same treatment.
 *
 * Every worker is listened to from the moment it is spawned: its `error` event (a script that
 * fails to load or throws at startup fires it long before any attach) and its handshake
 * messages (`ready` once evaluated, `transform` once the engine routes frames to it). Together
 * with the transform assignment they let `status()` name the exact stage a silent tap failed
 * at, for the host-facing debug payload: a phone's console is unreachable.
 *
 * Feature-detected: without `RTCRtpScriptTransform` or `Worker`, `attach` is a no-op and the
 * wire field stays absent - omitted, never zero-filled.
 */
export class EncodedFrameStatsTap implements EncodedFrameTap {
  private readonly tracker = new EncodedFrameSizeTracker();
  private readonly supported: boolean;
  private worker: Worker | null = null;
  private attachedTo: TransformableReceiver | null = null;
  /** A worker spawned ahead of the next attach, so the transform can be set synchronously. */
  private spare: Worker | null = null;
  private spareArriving: Promise<Worker | null> | null = null;
  /** Bumped on every attach/detach so a worker that resolves late for a stale attach is dropped. */
  private generation = 0;
  private lastError: string | null = null;
  private readonly handshakes = new WeakMap<Worker, WorkerHandshake>();

  constructor(private readonly workerFactory: EncodedFrameWorkerFactory = defaultWorkerFactory) {
    this.supported = EncodedFrameStatsTap.isSupported();
    if (this.supported) this.prepareSpare();
  }

  /** True when this engine can run the tap at all. */
  public static isSupported(): boolean {
    return scriptTransformCtor() !== null && typeof Worker === 'function';
  }

  public attach(receiver: unknown): void {
    if (!this.supported) return;
    const target = receiver as TransformableReceiver;
    if (this.attachedTo === target) return;
    this.detach();
    this.attachedTo = target;
    const generation = ++this.generation;

    if (this.spare) {
      const worker = this.spare;
      this.spare = null;
      this.install(target, worker);
      this.prepareSpare();
      return;
    }
    // No spare yet (attach raced the first spawn): install as soon as it lands.
    (this.spareArriving ?? this.prepareSpare()).then((worker) => {
      if (!worker) return;
      if (generation !== this.generation || this.attachedTo !== target) {
        // The attach this spawn was for is gone; keep the worker as the next spare. The
        // spawn handler has usually already filed it as such, in which case leave it be.
        if (!this.spare) this.spare = worker;
        else if (this.spare !== worker) worker.terminate();
        return;
      }
      this.spare = null;
      this.install(target, worker);
      this.prepareSpare();
    });
  }

  public detach(): void {
    this.generation++;
    if (this.attachedTo) {
      try {
        this.attachedTo.transform = null;
      } catch {
        // A closed receiver rejects the assignment; nothing to release there.
      }
    }
    this.worker?.terminate();
    this.worker = null;
    this.attachedTo = null;
    this.tracker.reset();
  }

  public summarizeAndReset(): EncodedFrameSummary | undefined {
    return this.tracker.summarizeAndReset();
  }

  /**
   * Where the tap stands. Once attached the suffix names how far the worker got:
   * `worker-silent` (never evaluated - script load or startup failure with no error event),
   * `worker-ready` (evaluated, but the engine has not routed frames to it),
   * `transform-live` (frames are flowing through it).
   */
  public status(): string {
    if (!this.supported) return 'unsupported';
    if (this.lastError) return `failed: ${this.lastError}`;
    if (this.worker) {
      const hs = this.handshakes.get(this.worker);
      const stage = hs?.transform ? 'transform-live' : hs?.ready ? 'worker-ready' : 'worker-silent';
      return `attached:${stage}`;
    }
    if (this.attachedTo) return 'attaching';
    return this.spare ? 'ready' : 'preparing';
  }

  /** Put a worker-backed transform on the receiver; any failure is recorded, never thrown. */
  private install(target: TransformableReceiver, worker: Worker): void {
    const ctor = scriptTransformCtor();
    if (!ctor) return;
    try {
      target.transform = new ctor(worker);
      this.worker = worker;
      this.lastError = null;
    } catch (err) {
      this.lastError = String(err);
      worker.terminate();
      this.attachedTo = null;
    }
  }

  /** Wire a freshly spawned worker so nothing it says - or fails to say - is lost. */
  private listen(worker: Worker): void {
    this.handshakes.set(worker, { ready: false, transform: false });
    worker.onmessage = (event: MessageEvent<unknown>) => this.onWorkerMessage(worker, event.data);
    // A worker whose script fails to load (404, MIME, CSP) or throws at startup reports it only
    // here, and it does so as soon as it is spawned - long before any attach.
    worker.onerror = (event: ErrorEvent) => {
      this.lastError = `worker error: ${event.message || 'script failed to load'}`;
    };
  }

  private onWorkerMessage(worker: Worker, data: unknown): void {
    if (data === null || typeof data !== 'object') return;
    const message = data as Record<string, unknown>;
    const hs = this.handshakes.get(worker);
    switch (message['kind']) {
      case 'ready':
        if (hs) hs.ready = true;
        return;
      case 'transform':
        if (hs) hs.transform = true;
        return;
      case 'error':
        this.lastError = `worker: ${String(message['message'])}`;
        return;
      case 'batch': {
        const { kind: _kind, ...batch } = message;
        if (worker === this.worker && isEncodedFrameBatch(batch)) this.tracker.observeBatch(batch);
        return;
      }
      default:
        return;
    }
  }

  /** Spawn the next spare worker; a failed spawn is recorded and leaves the tap without one. */
  private prepareSpare(): Promise<Worker | null> {
    if (this.spareArriving) return this.spareArriving;
    let resolved: Promise<Worker>;
    try {
      resolved = Promise.resolve(this.workerFactory());
    } catch (err) {
      this.lastError = String(err);
      return Promise.resolve(null);
    }
    this.spareArriving = resolved.then(
      (worker) => {
        this.spareArriving = null;
        this.listen(worker);
        if (this.spare) {
          worker.terminate();
          return null;
        }
        this.spare = worker;
        return worker;
      },
      (err: unknown) => {
        this.spareArriving = null;
        this.lastError = String(err);
        return null;
      },
    );
    return this.spareArriving;
  }
}
